/**
 * The frame every screen sits in.
 *
 * A permanent sidebar on a workstation, a drawer behind a button below 68rem.
 * The sidebar is mounted once here rather than per route, which is what keeps
 * it visually still while the content area changes - a shell re-mounted per
 * screen flickers its active states on every navigation.
 *
 * The content area scrolls independently of the sidebar. On a 1080p screen the
 * project navigation and the version block must both stay reachable without
 * scrolling past a long page of capture sessions.
 */

import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { TaskCenter } from "@/features/workflow/TaskCenter";
import { BrandMark, BrandWordmark } from "@/components/icons";
import { paths } from "./paths";
import { Sidebar } from "./Sidebar";
import "./AppShell.css";

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const closeButton = useRef<HTMLButtonElement>(null);
  const main = useRef<HTMLElement>(null);

  // A drawer that survived navigation would cover the page just asked for.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Long screens otherwise keep their scroll position when you move to a
  // different one, which reads as the new page having loaded halfway down.
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <div className={`shell${drawerOpen ? " shell--drawer-open" : ""}`}>
      <a className="skip-link" href="#main">
        İçeriğe geç
      </a>

      <div id="shell-sidebar" className="shell__sidebar">
        {/*
          The drawer needs a way out that a reader can see. The scrim behind it
          closes it too, but an invisible target is not an affordance - on a
          phone the only visible escape was to guess.
        */}
        <button
          type="button"
          className="shell__drawer-close"
          aria-label="Menüyü kapat"
          ref={closeButton}
          onClick={() => setDrawerOpen(false)}
        >
          <span aria-hidden="true">✕</span>
        </button>
        <Sidebar onNavigate={() => setDrawerOpen(false)} />
      </div>

      {drawerOpen && (
        <button
          type="button"
          className="shell__scrim"
          aria-label="Menüyü kapat"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <main id="main" className="shell__main" ref={main}>
        {/*
          One header row, in the flow rather than floating over it.

          The controls were once absolutely positioned top-right and landed on
          top of every screen's own header actions. They are two, deliberately:
          the task centre is *state* - what is running now - and the bell is
          *events* - a record you can mark read. Tasks sit first because "is it
          done yet?" is the more frequent question.

          Below the sidebar breakpoint there used to be a sticky bar holding
          only a "Menü" button, and then this row beneath it - two stacked
          strips before any page content, with no product identity in either.
          A 390px phone started its content about 175px down and a tablet
          rather more. This is the same row in both layouts: on a workstation
          the sidebar carries the brand and the menu button is not needed, so
          only the two controls show.
        */}
        <div className="shell__topbar">
          <Link to={paths.home()} className="shell__brand" aria-label="SentyCap ana sayfa">
            <BrandMark size={26} id="shell" />
            <BrandWordmark />
          </Link>
          <div className="shell__topbar-actions">
            <TaskCenter />
            <NotificationBell />
          </div>
          <button
            type="button"
            className="btn btn--ghost shell__menu"
            aria-expanded={drawerOpen}
            aria-controls="shell-sidebar"
            onClick={() => setDrawerOpen((open) => !open)}
          >
            Menü
          </button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
